import { useMemo, useState } from "react";
import { Search, AlertTriangle, Sparkles } from "lucide-react";
import { dictionary, Language } from "../../../utils/dictionary";
import { AdTableRow } from "../../../services/meta/metaService";
import { Product } from "../../products/ProductInterface";

interface ProductsDashboardProps {
  products: Product[];
  ads: AdTableRow[];
  language: Language;
  onSelectProduct: (id: string) => void;
  onOpenUnassigned: () => void;
}

export const ProductsDashboard = ({
  products,
  ads,
  language,
  onSelectProduct,
  onOpenUnassigned,
}: ProductsDashboardProps) => {
  const [query, setQuery] = useState("");

  const { withAds, withoutAds, unassignedCount } = useMemo(() => {
    const countByProduct: Record<string, number> = {};
    let unassigned = 0;
    ads.forEach((ad) => {
      if (!ad.product_id) unassigned += 1;
      else countByProduct[ad.product_id] = (countByProduct[ad.product_id] ?? 0) + 1;
    });
    const q = query.trim().toLowerCase();
    const match = (p: Product) => (q ? p.name.toLowerCase().includes(q) : true);
    const withAds: { product: Product; adCount: number }[] = [];
    const withoutAds: Product[] = [];
    products.filter(match).forEach((p) => {
      const count = countByProduct[p.id] ?? 0;
      if (count > 0) withAds.push({ product: p, adCount: count });
      else withoutAds.push(p);
    });
    withAds.sort((a, b) => b.adCount - a.adCount);
    return { withAds, withoutAds, unassignedCount: unassigned };
  }, [products, ads, query]);

  if (products.length === 0) {
    return (
      <div className="p-8">
        <Hero language={language} query={query} onQueryChange={setQuery} />
        <div className="bg-white rounded-xl border border-gray-200 py-16 text-center mt-6">
          <p className="text-sm font-semibold text-gray-900 mb-1">
            {dictionary["performance-empty-no-products-title"]?.[language] ?? "Sin productos"}
          </p>
          <p className="text-xs text-gray-500 max-w-md mx-auto">
            {dictionary["performance-empty-no-products-subtext"]?.[language] ?? "Creá un producto desde la sección Productos."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <Hero language={language} query={query} onQueryChange={setQuery} />

      {unassignedCount > 0 && (
        <UnassignedBanner count={unassignedCount} language={language} onClick={onOpenUnassigned} />
      )}

      {withAds.length > 0 && (
        <section className="mt-6">
          <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-3">
            {dictionary["performance-with-ads"]?.[language] ?? "Con anuncios"} ({withAds.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {withAds.map(({ product, adCount }) => (
              <ProductCard key={product.id} product={product} adCount={adCount} language={language} onClick={() => onSelectProduct(product.id)} />
            ))}
            {unassignedCount > 0 && (
              <UnassignedCard count={unassignedCount} language={language} onClick={onOpenUnassigned} />
            )}
          </div>
        </section>
      )}

      {withoutAds.length > 0 && (
        <section className="mt-8">
          <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-3">
            {dictionary["performance-without-ads"]?.[language] ?? "Sin anuncios todavía"} ({withoutAds.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {withoutAds.map((product) => (
              <ProductCard key={product.id} product={product} adCount={0} language={language} muted />
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

const Hero = ({
  language,
  query,
  onQueryChange,
}: {
  language: Language;
  query: string;
  onQueryChange: (q: string) => void;
}) => (
  <div className="flex items-start justify-between gap-4 mb-4">
    <div>
      <h1 className="text-[22px] font-semibold text-gray-900 tracking-tight">
        {dictionary["performance-dashboard-title"]?.[language] ?? "Performance por producto"}
      </h1>
      <p className="text-[13px] text-gray-500 mt-1 max-w-xl">
        {dictionary["performance-dashboard-subtitle"]?.[language] ?? "Elegí un producto para ver su performance."}
      </p>
    </div>
    <div className="relative flex-shrink-0">
      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
      <input
        type="text"
        placeholder={dictionary["performance-search-placeholder"]?.[language] ?? "Buscar productos…"}
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        className="h-9 w-56 pl-9 pr-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-fluxi"
      />
    </div>
  </div>
);

const UnassignedBanner = ({
  count,
  language,
  onClick,
}: {
  count: number;
  language: Language;
  onClick: () => void;
}) => (
  <div
    className="flex items-center justify-between gap-4 p-4 rounded-xl border"
    style={{
      background: "linear-gradient(to right, #FFF7ED, #FFFBEB)",
      borderColor: "#FED7AA",
    }}
  >
    <div className="flex items-center gap-3">
      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center">
        <AlertTriangle className="w-5 h-5 text-white" />
      </div>
      <div>
        <p className="text-sm font-semibold text-gray-900">
          {(dictionary["performance-banner-title"]?.[language] ?? "{N} anuncios sin asignar").replace("{N}", String(count))}
        </p>
        <p className="text-xs text-gray-600">
          {dictionary["performance-banner-subtext"]?.[language] ?? "Asignalos para ver competidores del nicho."}
        </p>
      </div>
    </div>
    <button
      type="button"
      onClick={onClick}
      className="flex-shrink-0 h-9 px-3 bg-gray-900 text-white text-xs font-semibold rounded-lg hover:opacity-90 inline-flex items-center gap-1"
    >
      <Sparkles className="w-3 h-3" />
      {(dictionary["performance-banner-cta"]?.[language] ?? "Asignar {N} anuncios").replace("{N}", String(count))}
    </button>
  </div>
);

const ProductCard = ({
  product,
  adCount,
  language,
  muted,
  onClick,
}: {
  product: Product;
  adCount: number;
  language: Language;
  muted?: boolean;
  onClick?: () => void;
}) => {
  const thumb = product.images?.[0];
  const disabled = muted || !onClick;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`flex items-start gap-3 p-3 bg-white rounded-xl border border-gray-200 text-left transition-all ${
        disabled
          ? "opacity-50 cursor-not-allowed"
          : "hover:border-fluxi/60 hover:shadow-[0_4px_16px_rgba(67,104,229,0.08)]"
      }`}
    >
      <div className="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden bg-gray-100">
        {thumb ? (
          <img src={thumb} alt={product.name} className="w-full h-full object-cover" />
        ) : null}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 mb-1">
          <p className="text-[13px] font-semibold text-gray-900 truncate min-w-0">{product.name}</p>
          <span
            className={`flex-shrink-0 inline-flex items-center gap-1 h-6 px-2 rounded-full bg-white border border-gray-200 text-[11px] font-semibold ${
              adCount > 0 ? "text-gray-900" : "text-gray-400"
            }`}
          >
            {adCount > 0 && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />}
            {adCount} {dictionary["performance-ads-count"]?.[language] ?? "ads"}
          </span>
        </div>
        {product.description && (
          <p className="text-[11px] text-gray-500 line-clamp-2">{product.description}</p>
        )}
      </div>
    </button>
  );
};

const UnassignedCard = ({
  count,
  language,
  onClick,
}: {
  count: number;
  language: Language;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className="flex items-start gap-3 p-3 rounded-xl text-left transition-all border border-dashed hover:shadow-[0_4px_16px_rgba(245,158,11,0.12)]"
    style={{
      background: "linear-gradient(to bottom right, #FFF7ED, #FFFBEB)",
      borderColor: "#F59E0B80",
    }}
  >
    <div
      className="flex-shrink-0 w-20 h-20 rounded-lg flex items-center justify-center"
      style={{ background: "rgba(245,158,11,0.1)" }}
    >
      <span className="text-[22px] font-bold text-amber-600 tabular-nums">{count}</span>
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[13px] font-semibold text-gray-900 mb-1">
        {dictionary["performance-unassigned-title"]?.[language] ?? "Sin asignar"}
      </p>
      <p className="text-[11px] text-gray-600 line-clamp-2">
        {dictionary["performance-unassigned-subtext"]?.[language] ?? "Anuncios sin producto que coincida"}
      </p>
    </div>
  </button>
);

export default ProductsDashboard;
